import gsap from 'gsap';
import appStore from '../utils/state';
import onKlaviyoNewsletterFormSubmit from '../utils/onKlaviyoNewsletterFormSubmit';
import ButtonModular from './button-modular';

export default class OutOfStockForm extends HTMLElement {
  private form: HTMLFormElement | null = null;
  private emailInput: HTMLInputElement | null = null;
  private nameInput: HTMLInputElement | null = null;
  private newsletterCheckbox: HTMLInputElement | null = null;
  private nameContainer: HTMLDivElement | null = null;
  private nameContent: HTMLDivElement | null = null;
  private submitButton: ButtonModular | null = null;
  private submitButtonElement: HTMLButtonElement | null = null;
  private infoElement: HTMLParagraphElement | null = null;
  private successElement: HTMLParagraphElement | null = null;
  private fieldsElement: HTMLDivElement | null = null;

  private klaviyoPublicApiKey: string | null = null;
  private klaviyoNewsletterListId: string | null = null;
  private klaviyoNewsletterSourceId: string | null = null;

  private isSubmitting = false;
  private isSubmitted = false;
  private isNameFieldOpen = false;
  private nameContentHeight = 0;

  private openNameTl: gsap.core.Timeline | null = null;
  private closeNameTl: gsap.core.Timeline | null = null;
  private successTl: gsap.core.Timeline | null = null;

  private unsubscribeFromModalOpen: (() => void) | null = null;
  private unsubscribeFromOutStockVariantChange: (() => void) | null = null;

  private onFormInputBound = this.onFormInput.bind(this);
  private onNewsletterChangeBound = this.onNewsletterChange.bind(this);
  private onFormSubmitBound = this.onFormSubmit.bind(this);
  private onSubmitButtonClickBound = this.onSubmitButtonClick.bind(this);
  private onResizeBound = this.onResize.bind(this);

  connectedCallback(): void {
    this.klaviyoPublicApiKey = this.dataset.klaviyoPublicApiKey || null;
    this.klaviyoNewsletterListId = this.dataset.klaviyoNewsletterListId || null;
    this.klaviyoNewsletterSourceId = this.dataset.klaviyoNewsletterSourceId || null;

    this.form = this.querySelector('[data-out-of-stock-form-ref="form"]');
    this.emailInput = this.querySelector('[data-out-of-stock-form-ref="email"]');
    this.nameInput = this.querySelector('[data-out-of-stock-form-ref="name"]');
    this.newsletterCheckbox = this.querySelector('[data-out-of-stock-form-ref="newsletter-checkbox"]');
    this.nameContainer = this.querySelector('[data-out-of-stock-form-ref="name-container"]');
    this.nameContent = this.querySelector('[data-out-of-stock-form-ref="name-content"]');
    this.submitButton = this.querySelector('[data-out-of-stock-form-ref="submit-button"]');
    this.submitButtonElement = this.submitButton?.querySelector('[data-button-modular-ref="button"]') ?? null;
    this.infoElement = this.querySelector('[data-out-of-stock-form-ref="info"]');
    this.successElement = this.querySelector('[data-out-of-stock-form-ref="success"]');
    this.fieldsElement = this.querySelector('[data-out-of-stock-form-ref="fields"]');

    this.addEventListeners();
    this.subscribe();
    this.onResize();
    this.updateButtonState();
  }

  addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);

    if (this.form) {
      this.form.addEventListener('input', this.onFormInputBound);
      this.form.addEventListener('submit', this.onFormSubmitBound);
    }

    if (this.newsletterCheckbox) {
      this.newsletterCheckbox.addEventListener('change', this.onNewsletterChangeBound);
    }

    if (this.submitButtonElement) {
      this.submitButtonElement.addEventListener('click', this.onSubmitButtonClickBound);
    }
  }

  removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    if (this.form) {
      this.form.removeEventListener('input', this.onFormInputBound);
      this.form.removeEventListener('submit', this.onFormSubmitBound);
    }

    if (this.newsletterCheckbox) {
      this.newsletterCheckbox.removeEventListener('change', this.onNewsletterChangeBound);
    }

    if (this.submitButtonElement) {
      this.submitButtonElement.removeEventListener('click', this.onSubmitButtonClickBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromModalOpen = appStore.subscribeKey('isOutOfStockModalOpen', (isOpen) => {
      if (isOpen) {
        this.reset();
      }
    });

    this.unsubscribeFromOutStockVariantChange = appStore.subscribeKey('outOfStockModalProduct', () => {
      if (appStore.getState().isOutOfStockModalOpen) {
        this.reset();
      }
    });
  }

  unsubscribe(): void {
    if (this.unsubscribeFromModalOpen) {
      this.unsubscribeFromModalOpen();
    }
    if (this.unsubscribeFromOutStockVariantChange) {
      this.unsubscribeFromOutStockVariantChange();
    }
  }

  isValidEmail(email: string | undefined): boolean {
    if (!email) {
      return false;
    }

    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    return emailRegex.test(email.trim());
  }

  isFormValid(): boolean {
    const email = this.emailInput?.value;
    const isEmailValid = this.isValidEmail(email);
    const isNewsletterChecked = this.newsletterCheckbox?.checked ?? false;
    const name = this.nameInput?.value.trim() ?? '';

    if (!isEmailValid) {
      return false;
    }

    if (isNewsletterChecked && name.length === 0) {
      return false;
    }

    return true;
  }

  updateButtonState(): void {
    if (this.isSubmitted || !this.submitButton) {
      return;
    }

    if (this.isFormValid()) {
      this.submitButton.gotToStateIndex(1);
    } else {
      this.submitButton.gotToStateIndex(0);
    }
  }

  onFormInput(): void {
    this.updateButtonState();
  }

  onNewsletterChange(): void {
    const isChecked = this.newsletterCheckbox?.checked ?? false;

    if (isChecked) {
      this.openNameField();
    } else {
      this.closeNameField();
    }

    this.updateButtonState();
  }

  onResize(): void {
    if (this.nameContent) {
      this.nameContentHeight = this.nameContent.getBoundingClientRect().height;
    }
  }

  openNameField(): void {
    if (!this.nameContainer || !this.nameInput || this.isNameFieldOpen) {
      return;
    }

    this.isNameFieldOpen = true;
    this.nameInput.required = true;
    this.onResize();

    if (this.closeNameTl) {
      this.closeNameTl.kill();
    }
    if (this.openNameTl) {
      this.openNameTl.kill();
    }

    this.openNameTl = gsap.timeline();
    this.openNameTl.to(this.nameContainer, { height: this.nameContentHeight, duration: 0.6, ease: 'power3.out' }, 0);
    this.openNameTl.fromTo(
      this.nameInput,
      { y: 10, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.6, ease: 'expo.out' },
      0,
    );
  }

  closeNameField(): void {
    if (!this.nameContainer || !this.nameInput || !this.isNameFieldOpen) {
      if (this.nameInput) {
        this.nameInput.required = false;
        this.nameInput.value = '';
      }
      return;
    }

    this.isNameFieldOpen = false;
    this.nameInput.required = false;
    this.nameInput.value = '';

    if (this.openNameTl) {
      this.openNameTl.kill();
    }
    if (this.closeNameTl) {
      this.closeNameTl.kill();
    }

    this.closeNameTl = gsap.timeline();
    this.closeNameTl.to(this.nameContainer, { height: 0, duration: 0.6, ease: 'expo.out' }, 0);
    this.closeNameTl.to(this.nameInput, { autoAlpha: 0, duration: 0.3, ease: 'power2.out' }, 0);
  }

  async subscribeToBackInStock(email: string, variantId: number): Promise<boolean> {
    if (!this.klaviyoPublicApiKey) {
      return false;
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoPublicApiKey}`,
      {
        method: 'POST',
        headers: { revision: '2025-04-15', 'content-type': 'application/json' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                  },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      },
    );

    return response.ok;
  }

  onSubmitButtonClick(event: MouseEvent): void {
    if (!this.isSubmitted) {
      return;
    }

    event.preventDefault();
    appStore.actions.closeOutOfStockModal();
  }

  async onFormSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (this.isSubmitting || this.isSubmitted || !this.isFormValid()) {
      return;
    }

    const formData = new FormData(this.form as HTMLFormElement);
    const nickname = formData.get('nickname') as string;

    if (nickname) {
      return;
    }

    const email = this.emailInput?.value.trim() ?? '';
    const name = this.nameInput?.value.trim() ?? '';
    const isNewsletterChecked = this.newsletterCheckbox?.checked ?? false;
    const variantId = appStore.getState().outOfStockModalProduct?.id;

    if (!variantId) {
      return;
    }

    this.isSubmitting = true;

    try {
      const backInStockSuccess = await this.subscribeToBackInStock(email, variantId);

      if (!backInStockSuccess) {
        return;
      }

      if (isNewsletterChecked && this.klaviyoPublicApiKey && this.klaviyoNewsletterListId) {
        await onKlaviyoNewsletterFormSubmit({
          email,
          firstName: name,
          klaviyoPublicApiKey: this.klaviyoPublicApiKey,
          klaviyoNewsletterListId: this.klaviyoNewsletterListId,
          klaviyoNewsletterSourceId: this.klaviyoNewsletterSourceId || '',
        });
      }

      this.onSuccess();
    } catch (error) {
      console.error(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  onSuccess(): void {
    this.isSubmitted = true;

    if (this.successTl) {
      this.successTl.kill();
    }

    this.successTl = gsap.timeline();

    if (this.fieldsElement) {
      this.successTl.to(this.fieldsElement, { autoAlpha: 0, duration: 0.3, ease: 'power3.out' }, 0);
    }

    if (this.infoElement) {
      this.successTl.to(this.infoElement, { autoAlpha: 0, duration: 0.3, ease: 'power3.out' }, 0);
    }

    if (this.successElement) {
      this.successTl.to(this.successElement, { autoAlpha: 1, duration: 0.3, ease: 'power3.out' }, 0.4);
    }

    if (this.submitButton) {
      this.submitButton.gotToStateIndex(2);
    }
  }

  reset(): void {
    this.isSubmitting = false;
    this.isSubmitted = false;

    if (this.openNameTl) {
      this.openNameTl.kill();
    }
    if (this.closeNameTl) {
      this.closeNameTl.kill();
    }
    if (this.successTl) {
      this.successTl.kill();
    }

    this.form?.reset();

    if (this.newsletterCheckbox) {
      this.newsletterCheckbox.checked = false;
    }

    this.isNameFieldOpen = false;

    if (this.nameInput) {
      this.nameInput.required = false;
      this.nameInput.value = '';
      gsap.set(this.nameInput, { autoAlpha: 0, y: 10 });
    }

    if (this.nameContainer) {
      gsap.set(this.nameContainer, { height: 0 });
    }

    if (this.fieldsElement) {
      gsap.set(this.fieldsElement, { autoAlpha: 1 });
    }

    if (this.infoElement) {
      gsap.set(this.infoElement, { autoAlpha: 1 });
    }

    if (this.successElement) {
      gsap.set(this.successElement, { autoAlpha: 0 });
    }

    if (this.submitButton) {
      this.submitButton.gotToStateIndex(0);
    }

    this.onResize();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();

    if (this.openNameTl) {
      this.openNameTl.kill();
    }
    if (this.closeNameTl) {
      this.closeNameTl.kill();
    }
    if (this.successTl) {
      this.successTl.kill();
    }
  }
}

customElements.define('out-of-stock-form', OutOfStockForm);
